import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Activity,
  Users,
  Clock,
  Filter,
  X,
} from "lucide-react";
import UserMenu from "../components/UserMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const ACTION_COLORS = {
  login: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  login_failed: "bg-red-500/15 text-red-400 border-red-500/30",
  logout: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  upload: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  delete: "bg-red-500/15 text-red-400 border-red-500/30",
  create: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  update: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  deactivate: "bg-red-500/15 text-red-400 border-red-500/30",
  compose: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  reply: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  sync: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  impersonate: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  change_password: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  assign_roles: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
};

const MODULE_LABELS = {
  auth: "Authentication",
  po: "Purchase Orders",
  bom: "Bill of Materials",
  user: "User Management",
  role: "Roles & Permissions",
  email: "Email",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const pageSize = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append("search", search);
      if (moduleFilter && moduleFilter !== "all") params.append("module", moduleFilter);
      if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
      const res = await axios.get(`${API}/audit/logs?${params}`, getAuthHeaders());
      setLogs(res.data.logs || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [page, search, moduleFilter, actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, aRes] = await Promise.all([
          axios.get(`${API}/audit/modules`, getAuthHeaders()),
          axios.get(`${API}/audit/actions`, getAuthHeaders()),
        ]);
        setModules(mRes.data.modules || []);
        setActions(aRes.data.actions || []);
      } catch {}
    })();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const clearFilters = () => {
    setSearch("");
    setModuleFilter("all");
    setActionFilter("all");
    setPage(1);
  };

  const hasFilters = search || (moduleFilter && moduleFilter !== "all") || (actionFilter && actionFilter !== "all");

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              data-testid="audit-search-input"
              placeholder="Search by user, action, target..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>
        </form>

        <Select value={moduleFilter} onValueChange={(v) => { setModuleFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="audit-module-filter" className="w-[160px] bg-white/5 border-white/10 text-white">
            <Filter className="w-3.5 h-3.5 mr-2 text-white/40" />
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {modules.map((m) => (
              <SelectItem key={m} value={m}>{MODULE_LABELS[m] || m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="audit-action-filter" className="w-[160px] bg-white/5 border-white/10 text-white">
            <Filter className="w-3.5 h-3.5 mr-2 text-white/40" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-white/60 hover:text-white">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}

        <Button
          data-testid="audit-refresh-btn"
          variant="outline"
          size="icon"
          onClick={fetchLogs}
          className="border-white/10 text-white/60 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Summary */}
      <div className="text-sm text-white/50">{total} total entries</div>

      {/* Table */}
      <div className="rounded-lg border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/60 w-[140px]">Time</TableHead>
              <TableHead className="text-white/60">User</TableHead>
              <TableHead className="text-white/60 w-[110px]">Action</TableHead>
              <TableHead className="text-white/60 w-[120px]">Module</TableHead>
              <TableHead className="text-white/60">Target</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-white/40">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading...
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-white/40">
                  No audit logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} className="border-white/5 hover:bg-white/[0.03]" data-testid={`audit-log-row-${log.id}`}>
                  <TableCell className="text-white/60 text-xs font-mono">
                    {formatTimestamp(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <div className="text-white text-sm">{log.user_name}</div>
                    <div className="text-white/40 text-xs">{log.user_email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs font-medium ${ACTION_COLORS[log.action] || "bg-white/10 text-white/70 border-white/20"}`}
                    >
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-white/70 text-sm">{MODULE_LABELS[log.module] || log.module}</span>
                  </TableCell>
                  <TableCell>
                    {log.target_name ? (
                      <span className="text-white/60 text-sm">{log.target_name}</span>
                    ) : (
                      <span className="text-white/30 text-sm">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-white/50">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              data-testid="audit-prev-page"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="border-white/10 text-white/60"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              data-testid="audit-next-page"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="border-white/10 text-white/60"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function OnlineUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await axios.get(`${API}/audit/online-users`, getAuthHeaders());
      setUsers(usersRes.data.users || []);
    } catch {
      toast.error("Failed to load online users");
    }
    try {
      const rolesRes = await axios.get(`${API}/roles`, getAuthHeaders());
      const roleMap = {};
      const roleList = rolesRes.data.roles || rolesRes.data || [];
      roleList.forEach((r) => {
        roleMap[r.id] = r.name;
      });
      setRoles(roleMap);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const onlineCount = users.filter((u) => u.is_online).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-sm font-medium" data-testid="online-count">
              {onlineCount} Online
            </span>
          </div>
          <span className="text-white/40 text-sm">{users.length} total users</span>
        </div>
        <Button
          data-testid="online-refresh-btn"
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-white/10 text-white/60 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/60 w-[60px]">Status</TableHead>
              <TableHead className="text-white/60">User</TableHead>
              <TableHead className="text-white/60">Role</TableHead>
              <TableHead className="text-white/60">Last Seen</TableHead>
              <TableHead className="text-white/60">Last Login</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-white/40">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading...
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-white/40">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} className="border-white/5 hover:bg-white/[0.03]" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${
                        u.is_online ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" : "bg-white/20"
                      }`}
                      data-testid={`user-status-${u.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="text-white text-sm">{u.name}</div>
                    <div className="text-white/40 text-xs">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.role_ids || []).map((rid) => (
                        <Badge
                          key={rid}
                          variant="outline"
                          className="text-xs bg-white/5 text-white/70 border-white/15"
                        >
                          {roles[rid] || rid}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-white/60 text-sm">
                    {u.is_online ? (
                      <span className="text-emerald-400 font-medium">Active now</span>
                    ) : (
                      formatTimestamp(u.last_seen)
                    )}
                  </TableCell>
                  <TableCell className="text-white/50 text-xs font-mono">
                    {formatTimestamp(u.last_login)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AuditModule({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("logs");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800" data-testid="audit-module">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="audit-back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/select-module")}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-amber-400" />
              <h1 className="text-lg font-semibold text-white">Audit & Activity</h1>
            </div>
          </div>
          <UserMenu user={user} onLogout={onLogout} variant="header" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger
              data-testid="tab-audit-logs"
              value="logs"
              className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60"
            >
              <Clock className="w-4 h-4 mr-2" />
              Audit Logs
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-online-users"
              value="online"
              className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60"
            >
              <Users className="w-4 h-4 mr-2" />
              Online Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="logs">
            <AuditLogs />
          </TabsContent>
          <TabsContent value="online">
            <OnlineUsers />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
